import * as _ from 'lodash';
import * as moment from 'moment';
import { StudentSupportStatuses } from '../../../../ng2/shared/constants/student-support-statuses.constant';
import nvSharedModelsModule from '../nv.shared.models.module';

angular
  .module(nvSharedModelsModule)

  .factory('StudentSupport', function(NvBaseModel, PublicConfig, $http, UtilitiesService, Support, ImStudentSupport) {
    const PERMISSIONING_MODEL_NAME = 'STUDENT_SUPPORT';
    const relationToUserPath = 'assignedBy.userId';

    const StudentSupport = NvBaseModel(
      '/v1/studentSupports/:id',
      { id: '@_id', schoolId: '@schoolId' },
      {
        _queryBySupportId: {
          method: 'GET',
          isArray: true,
          transformResponse(data) {
            const json = angular.fromJson(data);
            // if the request is cancelled, json will be null
            if (json) return json.data;
          },
          params: {
            where: '@where',
          },
        },
      },
      null,
      PERMISSIONING_MODEL_NAME,
      relationToUserPath,
    );

    StudentSupport.queryBySupportId = function(supportId) {
      return StudentSupport._queryBySupportId({
        where: JSON.stringify({ 'support.supportId': supportId }),
      });
    };

    /**
     * ALL methods in the extend have been migrated to ImStudentSupport as static methods
     * that in addition to params below, also take a full studentSupport - JYR
     */
    StudentSupport.extend({
      getStudentIdFrom_Id() {
        const self = this;
        const studentId = self.student.studentId;
        return studentId && studentId.replace(self.schoolId, '');
      },

      hasStarted() {
        const self = this;
        const now = moment();
        const nowFormatted = moment(now, 'YYYY-MM-DD');
        const startsOnFormatted = moment(self.startsOn, 'YYYY-MM-DD');
        const check1 = nowFormatted.isSame(startsOnFormatted, 'day');
        const diff = nowFormatted.diff(startsOnFormatted);
        const check2 = diff > 0;
        const check = check1 || check2;
        return check;
      },

      getCategoryHumanName() {
        const self = this;
        const support = new Support(self.support);
        return support.getCategoryHumanName(support.category, support.metaData);
      },

      getFrontendStatus(optBackendStatus) {
        const self = this;
        const isDeleted = self.isDeleted(optBackendStatus);
        if (isDeleted) return StudentSupportStatuses.frontend.REMOVED;

        const isCompleted = self.isCompleted(optBackendStatus);
        if (isCompleted) return StudentSupportStatuses.backend.COMPLETED;

        if (self.hasStarted()) {
          return StudentSupportStatuses.frontend.IN_PROGRESS;
        } else {
          return StudentSupportStatuses.frontend.NOT_STARTED;
        }
      },

      isDeleted(optBackendStatus) {
        const self = this;
        const backendStatus = optBackendStatus || self.status;
        return backendStatus === StudentSupportStatuses.backend.DELETED;
      },

      isCompleted(optBackendStatus) {
        const self = this;
        const backendStatus = optBackendStatus || self.status;
        return backendStatus === StudentSupportStatuses.backend.COMPLETED;
      },

      isActive(optBackendStatus) {
        const self = this;
        const backendStatus = optBackendStatus || self.status;
        return backendStatus === StudentSupportStatuses.backend.ACTIVE;
      },

      getStartsOnString() {
        const self = this;
        const format = 'MM/DD/YYYY';
        return moment(self.startsOn).format(format);
      },

      getCompletedAtString() {
        const self = this;
        const format = 'MM/DD/YYYY';
        return self.completedAt && moment(self.completedAt).format(format);
      },

      getAssignedOnString() {
        const self = this;
        const format = 'MM/DD/YYYY';
        return moment(self.assignedOn).format(format);
      },

      getReviewOnString() {
        const self = this;
        const format = 'MM/DD/YYYY';
        return moment(self.getLastReview()).format(format);
      },

      getLastReview() {
        const self = this;
        return self.reviewOn.length ? self.reviewOn[self.reviewOn.length - 1] : undefined;
      },

      needsReview() {
        const self = this;
        const lastReview = self.getLastReview();
        let needsReview = false;

        if (lastReview) {
          needsReview = moment().isAfter(lastReview);
        }

        return needsReview;
      },
    });

    /**
     * @param {Object} studentSupportIds - each key is a student._id and the value is the uuid to be assigned
     * as the _id on the backend to the studentSupport being created.
     * @param {Object} studentSupport - the studentSupport being created for each student. It should follow
     * the template created by StudentSupport<createTemplateFromSupport>.
     *
     * @example
     * example of payload sent to api by this method:
     * {
     *   studentSupportIds: {
     *     [student._id]: <UUID>
     *   },
     *   studentSupport: {
     *     schoolId: <school._id>
     *     startOn: <ISO date string>,
     *     support: {
     *       supportId: <UUID> of support
     *     }
     *   }
     * }
     */

    StudentSupport.bulkCreate = function(studentSupportIds, studentSupport) {
      return $http({
        method: 'POST',
        url: PublicConfig.NV_API_ORIGIN + '/v1/studentSupports/bulk/create',
        data: {
          studentSupportIds,
          studentSupport,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/html',
        },
      });
    };

    StudentSupport.bulkUpdate = function(studentSupportIds, status) {
      const data = {
        patches: [
          {
            studentSupportIds,
            patch: {
              status,
            },
          },
        ],
      };
      return $http({
        method: 'PATCH',
        url: PublicConfig.NV_API_ORIGIN + '/v1/studentSupports/update/bulk',
        data,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/html',
        },
      });
    };

    StudentSupport.createStudentSupportIdsFromStudentIds = function(_ids) {
      return _.reduce(
        _ids,
        (result, _id: string) => {
          result[_id] = UtilitiesService.createV4Uuid();
          return result;
        },
        {},
      );
    };

    StudentSupport.createTemplateFromSupport = function(support) {
      const template = {
        schoolId: support.schoolId,
        startsOn: support.schedule.startsOn,
        status: 'ACTIVE',
        support: {
          supportId: support._id,
          name: support.name,
          category: support.category,
          metaData: support.metaData,
        },
      };
      return template;
    };

    StudentSupport.hashStudentSupportsBySupportId = function(studentSupports) {
      return _.reduce(
        studentSupports,
        function(result, studentSupport: any) {
          const supportId = studentSupport.support.supportId;
          if (!result[supportId]) result[supportId] = [];
          result[supportId].push(studentSupport);
          return result;
        },
        {},
      );
    };

    StudentSupport.getActiveAndCompletedStudentSupports = function(studentSupports) {
      return _.filter(studentSupports, function(studentSupport: any) {
        return !studentSupport.isDeleted();
      });
    };

    StudentSupport.getActiveStudentSupports = function(studentSupports, optSupportCategoryKey, optExamSubjectKey) {
      return _.filter(studentSupports, function(studentSupport: any) {
        const studetSupportIsActive =
          studentSupport instanceof StudentSupport
            ? studentSupport.isActive()
            : studentSupport.status === StudentSupportStatuses.backend.ACTIVE;

        if (!studetSupportIsActive) return false;
        let include = true;

        if (optSupportCategoryKey) {
          const studentSupportCategory = studentSupport.support.category;
          include = studentSupportCategory === optSupportCategoryKey;
        }

        if (optExamSubjectKey) {
          const studentSupportExamSubject =
            studentSupport.support.metaData && studentSupport.support.metaData.examSubject;
          include = studentSupportExamSubject === optExamSubjectKey;
        }

        return include;
      });
    };

    StudentSupport.placePublicImStudentSupportStaticMethodsOnPrototype = function() {
      const ImStudentSupportStaticMethods = _.difference(Object.getOwnPropertyNames(ImStudentSupport), [
        'length',
        'name',
        'prototype',
      ]);

      // remove ImStudentSupport private methods
      _.remove(ImStudentSupportStaticMethods, staticMethod => {
        return staticMethod.indexOf('_') === 0;
      });

      /**
       * NOTE: The code below guards against methods being added to ImStudentSupport that already exist in the
       * StudentSupport factory.
       * However, there will be some time where some parts of the app will be able to use
       * ImStudentSupport, (wherever studentSupports are provided by the SDCStore) and other parts will need
       * to continue to call prototype methods here in the StudentSupport factory.
       *
       * TODO - Reenable this check when all code is using ImStudentSupport - JYR
       */

      // _.forEach(ImStudentSupportStaticMethods, (staticMethod) => {

      // if (StudentSupport.prototype[staticMethod]) {
      //   throw new Error(`ImStudentSupport.${staticMethod} static method already
      //   exists in ImStudentSupport.prototype`);
      // }

      //   StudentSupport.prototype[staticMethod] = function (...params) {
      //     params.unshift(this);
      //     return ImStudentSupport[staticMethod].apply(null, params);
      //   };
      // });
    };

    StudentSupport.placePublicImStudentSupportStaticMethodsOnPrototype();

    return StudentSupport;
  });
